"use client";
import { useRef } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useMotionTemplate,
  useReducedMotion,
  MotionValue,
  useSpring,
} from "framer-motion";

interface StackCardProps {
  child: React.ReactNode;
  index: number;
  totalLength: number;
  scrollYProgress: MotionValue<number>;
  stickyTop: string;
  bandHeight: number;
  scaleStep: number;
}
interface ScrollStackProps {
  children: React.ReactNode[];
  // CSS length the first card pins at (e.g. the pinned heading's height), so the
  // deck stacks *below* a sticky header instead of behind it. Falls back to 1vh.
  topOffset?: string;
  // Vertical offset (px) between each pinned card. Sized to a card's header band
  // so every earlier card keeps a labelled strip peeking above the next one.
  bandHeight?: number;
  // Per-card shrink of the pinned deck. 0 = flat, full-width strips (reference).
  scaleStep?: number;
  // Negative CSS length trimmed off the deck's tail. A short pinned heading
  // (bounded by the section) otherwise releases ~one card-height AFTER the deck,
  // so its lower line stays exposed as the cards scroll up. Trimming the tail
  // pulls the heading's release up to meet the deck's — the z-30 deck covers the
  // next section that the trim slides up, so the whole thing leaves as one block.
  endTrim?: string;
  // Row gap (px) between cards in the pre-pin flow, so consecutive cards breathe
  // instead of butting together before each one pins. Pinned positions are fixed
  // by `top`, so this only spaces the cards on their way in. Default 0.
  itemGap?: number;
}

//Stack Card Component
const StackCard = ({
  child,
  index,
  totalLength,
  scrollYProgress,
  stickyTop,
  bandHeight,
  scaleStep,
}: StackCardProps) => {
  const reduceMotion = useReducedMotion();

  // For Desktop target scale calculation
  const targetScale = 1 - (totalLength - index) * scaleStep;
  const progressStart = index / totalLength;
  const progressEnd = 1;
  const rawScale = useTransform(
    scrollYProgress,
    [progressStart, progressEnd],
    [1, targetScale],
  );
  // Buttery Smooth Engine
  const smoothScale = useSpring(rawScale, {
    stiffness: 100,
    damping: 20,
    restDelta: 0.001,
  });

  // Depth-of-field: a card rising in from below reads soft, then pulls into
  // focus over the stretch just before it lands on its pin — and re-softens on
  // the way back up, since it is driven by the same scroll value. The window is
  // one card's slice of the scroll; the first card's slice starts below 0, so it
  // clamps to sharp and never blurs.
  const maxBlur = reduceMotion ? 0 : 8;
  const rawBlur = useTransform(
    scrollYProgress,
    [progressStart - 1 / totalLength, progressStart],
    [maxBlur, 0],
  );
  const smoothBlur = useSpring(rawBlur, {
    stiffness: 120,
    damping: 24,
    restDelta: 0.01,
  });
  const filter = useMotionTemplate`blur(${smoothBlur}px)`;

  return (
    <motion.div
      style={{
        scale: smoothScale,
        filter,
        transformOrigin: "top center",
        top: `calc(${stickyTop} + ${index * bandHeight}px)`,
      }}
      // z-30 keeps the deck *above* the pinned heading (z-10), so on exit the
      // cards scroll up and over the heading instead of behind it.
      className="sticky w-full origin-top z-30"
    >
      {child}
    </motion.div>
  );
};

//  Main ScrollStack Component
const ScrollStack = ({
  children,
  topOffset = "1vh",
  bandHeight = 10,
  scaleStep = 0.05,
  endTrim,
  itemGap = 0,
}: ScrollStackProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });
  return (
    <div ref={containerRef} className="w-full">
      <div
        className="w-full flex flex-col lg:pb-15"
        style={{ marginBottom: endTrim, rowGap: itemGap }}
      >
        {children.map((child, index) => (
          <StackCard
            key={index}
            child={child}
            index={index}
            totalLength={children.length}
            scrollYProgress={scrollYProgress}
            stickyTop={topOffset}
            bandHeight={bandHeight}
            scaleStep={scaleStep}
          />
        ))}
      </div>
    </div>
  );
};

export default ScrollStack;
